import { useEffect, useRef, useState } from 'react'
import { Check, ChevronDown, ChevronLeft, ChevronRight, GripVertical, Pencil, RefreshCw, Save, Search, Settings, UserPlus, X } from 'lucide-react'

function DiscordIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
    </svg>
  )
}
import type { Event, SignUp } from './types'
import { MINIMAL_LAYOUT, fetchRosterDefault, type DefaultSquad } from '@/lib/squadTypes'
import { defaultSlots, roleOf, typeOf, useRosterCatalog, type SquadType } from '@/lib/rosterCatalog'
import EmojiGlyph from '@/components/ui/EmojiGlyph'
import { specKey, useMatchSpecs, type MatchSpec } from '@/lib/matchSpecs'
import { PlayerIcon } from '@/components/events/EventDetailPanel'
import { panelGhostButton, panelPrimaryButton } from '@/components/layout/Panel'
import { useLanguage } from '@/contexts/LanguageContext'

/**
 * Panelin geri kalanıyla aynı üç yüzey. Kadro ekranı kendi görünümünü
 * uydurmuyor: bant başlığı saç telli `font-display`, yüzeyler `rounded-sm`,
 * üstte açılan katmanlar `AccountMenu`'nün kapalı zeminini kullanıyor.
 */
const popoverSurface = 'rounded-sm border border-white/12 bg-[#16161a] shadow-xl'
const sectionLabel   = 'font-display text-[10px] font-bold uppercase tracking-[0.18em]'

/**
 * Kadro ekranının yüzeyleri opaktır. Bir zamanlar `bg-white/[0.035]` idi ve
 * arkadaki kontur haritası takım kartlarının içinden okunuyordu: onlarca isim
 * satırının üstünde doku gürültüdür. Renkler temanın kendi token'ları
 * (`card`, `popover`, `secondary`), yani panelin geri kalanıyla aynı gri aile.
 */
const sidePanel   = 'bg-popover'
const squadSurface = 'bg-card'
const rowSurface  = 'border-white/10 bg-secondary'

// ─── Types ────────────────────────────────────────────────────────────────────

const ACTIVE_CLASSES = new Set(['Inf','Tank','Recon','Comm','Arty','Artillery','Streamer','infantry','armour','recon','commander','artillery','tank'])
const BENCH_CLASSES  = new Set(['Bench'])

interface Slot  { id: string; role: string; userId: string | null }
// `icon` is kept for rosters saved before squad types moved to Settings; the
// type's emoji from the catalog is what gets drawn.
interface Squad { id: string; name: string; type: string; icon?: string; slots: Slot[] }

interface MemberOption {
  id: string; username: string; display_name: string
  avatar_url: string | null; team: string
}
interface ContextMenu { userId: string; x: number; y: number }
interface DraggingPlayer { userId: string; from: string | null; slotId?: string }

function makeSlots(defs: { role: string }[]): Slot[] {
  return defs.map(d => ({ id: `s${Date.now()}_${Math.random().toString(36).slice(2)}`, role: d.role, userId: null }))
}

/** A stored layout (Settings, or the built-in one) as fresh, empty squads. */
function toSquads(layout: DefaultSquad[]): Squad[] {
  return layout.map((sq, i) => ({
    id: `sq${i}_${Math.random().toString(36).slice(2, 8)}`,
    name: sq.name,
    type: sq.type,
    slots: makeSlots(sq.slots.map(role => ({ role }))),
  }))
}

const DAY   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']
const MONTH = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
function formatDate(ts: number) {
  const d = new Date(ts * 1000)
  return `${DAY[d.getDay()]}, ${d.getDate()} ${MONTH[d.getMonth()]} · ${d.toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'})}`
}

// ─── Icon helpers ─────────────────────────────────────────────────────────────

/** A slot's role as its emoji from Settings → Kadro Rolleri. */
function SlotIcon({ role, dim }: { role: string; dim?: boolean }) {
  const catalog = useRosterCatalog()
  const r = roleOf(catalog, role)
  if (!r?.emoji) return null
  return (
    <span title={r.name} className={`inline-flex shrink-0 transition-opacity ${dim ? 'opacity-20' : 'opacity-90'}`}>
      <EmojiGlyph emoji={r.emoji} size={16} />
    </span>
  )
}

/** A sub-class as a glyph: its icon, or its name when it has none. */
function SpecMark({ spec, size = 14, title, dim }: { spec: Pick<MatchSpec, 'name' | 'emoji'>; size?: number; title?: string; dim?: boolean }) {
  return (
    <span title={title ?? spec.name} className={`inline-flex shrink-0 items-center ${dim ? 'opacity-60' : ''}`}>
      {spec.emoji
        ? <EmojiGlyph emoji={spec.emoji} size={size} />
        : <span className="rounded-sm border border-white/15 px-1 py-px text-[9px] leading-none text-muted-foreground">{spec.name}</span>}
    </span>
  )
}

/** The sub-class a player picked when signing up, if any. */
function useSignUpSpec(player: Pick<SignUp, 'class_name' | 'spec_name'> | null) {
  const specs = useMatchSpecs()
  if (!player?.spec_name) return null
  const id = specKey(player.class_name, player.spec_name)
  // Removed from Settings since: still shown, by name.
  return specs.byId[id] ?? { id, name: player.spec_name, emoji: '', className: player.class_name }
}

/** An extra role on a slot: a sub-class from Settings, or (rosters saved
 *  before sub-classes) a role from the catalog. */
function ExtraRoleIcon({ roleId }: { roleId: string }) {
  const catalog = useRosterCatalog()
  const specs = useMatchSpecs()
  const spec = specs.byId[roleId]
  if (spec) return <SpecMark spec={spec} />
  const r = roleOf(catalog, roleId)
  if (!r?.emoji) return null
  return <span title={r.name} className="inline-flex shrink-0"><EmojiGlyph emoji={r.emoji} size={14} /></span>
}

/** A squad's type as its emoji from Settings → Takım Türleri. */
function SquadTypeIcon({ type, size = 16 }: { type: string; size?: number }) {
  const catalog = useRosterCatalog()
  const t = typeOf(catalog, type)
  if (!t?.emoji) return null
  return <EmojiGlyph emoji={t.emoji} size={size} />
}

function ConfirmDot({ status }: { status?: string }) {
  if (!status || status === 'pending') return <span className="h-1.5 w-1.5 rounded-full bg-white/20 shrink-0" title="Pending" />
  if (status === 'yes') return <span className="h-1.5 w-1.5 rounded-full bg-green-500 shrink-0" title="Onaylandı" />
  return <span className="h-1.5 w-1.5 rounded-full bg-red-500 shrink-0" title="Reddedildi" />
}

// ─── SquadBuilder ─────────────────────────────────────────────────────────────

interface Props { event: Event; apiBase?: string; preset?: 'full' | 'minimal'; canSend?: boolean }

export default function SquadBuilder({ event, apiBase, preset = 'minimal', canSend = true }: Props) {
  const { T } = useLanguage()
  const matchSpecs = useMatchSpecs()
  const catalog = useRosterCatalog()
  const base = apiBase ?? `/api/events/${event.id}`
  // Dashboard matches carry the class kind from Settings (is_bench is set);
  // Raid Helper sign-ups only have the class name, read against the old sets.
  const isActive = (s: SignUp) => s.is_bench !== undefined ? s.is_active : ACTIVE_CLASSES.has(s.class_name)
  const isBench  = (s: SignUp) => s.is_bench !== undefined ? s.is_bench : BENCH_CLASSES.has(s.class_name)
  const activePlayers = event.sign_ups.filter(isActive)
  const benchPlayers  = event.sign_ups.filter(isBench)
  const passivePlayers = event.sign_ups.filter(s => !isActive(s) && !isBench(s))
  const passivePlayerIds = new Set(passivePlayers.map(p => p.user_id))

  const [customPlayers, setCustomPlayers] = useState<SignUp[]>([])
  const [squads, setSquads] = useState<Squad[]>(() =>
    // A match roster starts empty; the default from Settings arrives below.
    toSquads(preset === 'minimal' ? MINIMAL_LAYOUT : []))
  // Not in a slot = bench when the roster is published; there is no separate
  // bench box to fill by hand. `removedPool` is the one way out: those players
  // are neither placed nor bench (someone who declined, for instance).
  const [unassigned,      setUnassigned]      = useState<string[]>([...activePlayers, ...benchPlayers].map(s => s.user_id))
  const [removedPool,     setRemovedPool]     = useState<string[]>([])
  const [declineReasons,  setDeclineReasons]  = useState<Record<string, string>>({})
  const [roleOverrides, setRoleOverrides] = useState<Record<string, string[]>>({})

  const [editingId, setEditingId]     = useState<string | null>(null)
  const [editName,  setEditName]      = useState('')
  const [dragOver,  setDragOver]      = useState<string | null>(null)  // slotId, '__unassigned__' or 'removed'
  const [dragging,  setDragging]      = useState<DraggingPlayer | null>(null)
  // Sürüklenen takım. Oyuncu sürüklemesinden ayrı: takım kartın üstünden
  // geçerken yer canlı değişir, bırakma yalnızca onu sabitler.
  const [draggingSquad, setDraggingSquad] = useState<string | null>(null)
  const squadSwapRef = useRef<string | null>(null)
  const [contextMenu, setContextMenu] = useState<ContextMenu | null>(null)

  // Mobilde sürükle-bırak çalışmaz (HTML5 DnD dokunmada yok). Bunun yerine
  // dokun-seç / dokun-yerleştir: bir oyuncuya dokununca "picked" olur, sonra
  // bir slota/takıma/havuza dokununca oraya taşınır. Masaüstünde DnD aynen kalır.
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)')
    const on = () => setIsMobile(mq.matches)
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])
  const [picked, setPicked] = useState<DraggingPlayer | null>(null)
  const togglePick = (dp: DraggingPlayer) =>
    setPicked(cur => (cur && cur.userId === dp.userId) ? null : dp)
  const [showAddSquad,    setShowAddSquad]    = useState(false)
  const [configSquadId,   setConfigSquadId]   = useState<string | null>(null)
  const [configButtonRect, setConfigButtonRect] = useState<DOMRect | null>(null)
  const [saving, setSaving]   = useState(false)
  const [saved,  setSaved]    = useState(false)
  const [loaded, setLoaded]   = useState(false)
  const [discordPosted,  setDiscordPosted]  = useState(false)  // true = Update mode
  const [discordSending, setDiscordSending] = useState(false)
  const [discordDone,    setDiscordDone]    = useState(false)
  const [confirmations,  setConfirmations]  = useState<Record<string, string>>({})
  const [showAddMember, setShowAddMember] = useState(false)
  const [memberSearch,  setMemberSearch]  = useState('')
  const [unassignedSearch, setUnassignedSearch] = useState('')
  const [memberOptions, setMemberOptions] = useState<MemberOption[]>([])
  const nameInputRef  = useRef<HTMLInputElement | null>(null)
  const addSquadRef   = useRef<HTMLDivElement | null>(null)
  const configRef     = useRef<HTMLDivElement | null>(null)

  const playerMap: Record<string,SignUp> = {}
  for (const s of [...event.sign_ups, ...customPlayers]) playerMap[s.user_id] = s

  // ── Load saved config + discord post status ──
  useEffect(() => {
    fetch(`${base}/discord-post`)
      .then(r => r.ok ? r.json() : { message_id: null })
      .then(d => setDiscordPosted(!!d.message_id))
      .catch(() => {})
  }, [event.id])

  useEffect(() => {
    setLoaded(false)
    fetch(`${base}/squads`)
      .then(r => r.ok ? r.json() : {squads:[],custom_players:[],role_overrides:{}})
      .then((data: { squads?: Squad[]; custom_players?: SignUp[]; role_overrides?: Record<string,string[]>; removed_players?: string[] }) => {
        const savedSquads = data?.squads ?? []
        const cp = data?.custom_players ?? []
        const ro = data?.role_overrides ?? {}
        const savedRemoved = data?.removed_players ?? []

        if (savedSquads.length > 0 && savedSquads[0]?.slots) {
          // New slot-based format
          const allDraggableIds = new Set([
            ...activePlayers.map(s => s.user_id),
            ...benchPlayers.map(s => s.user_id),
            ...cp.map(s => s.user_id),
          ])
          const validatedSquads = savedSquads.map(sq => ({
            ...sq,
            slots: (sq.slots ?? []).map(sl => ({
              ...sl,
              userId: sl.userId && allDraggableIds.has(sl.userId) ? sl.userId : null,
            })),
          }))
          const assigned = new Set(validatedSquads.flatMap(sq => sq.slots.map(sl => sl.userId).filter(Boolean) as string[]))
          const validRemoved = savedRemoved.filter(id => allDraggableIds.has(id) && !assigned.has(id))
          setSquads(validatedSquads)
          setRemovedPool(validRemoved)
          const takenIds = new Set([...assigned, ...validRemoved])
          setUnassigned([...new Set([...activePlayers, ...benchPlayers, ...cp].map(s => s.user_id))].filter(id => !takenIds.has(id)))
        } else {
          // Nothing saved for this match yet: open with the layout from
          // Settings → Varsayılan Kadro, if one is set.
          if (preset === 'full') {
            fetchRosterDefault().then(layout => { if (layout.length) setSquads(toSquads(layout)) })
          }
          setUnassigned([...activePlayers, ...benchPlayers].map(s => s.user_id))
          setRemovedPool([])
        }
        setCustomPlayers(cp)
        setRoleOverrides(ro)
        setLoaded(true)
      })
      .catch(() => setLoaded(true))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.id])

  useEffect(() => { if (editingId && nameInputRef.current) { nameInputRef.current.focus(); nameInputRef.current.select() } }, [editingId])
  useEffect(() => { if (!showAddMember) return; fetch('/api/members').then(r => r.ok ? r.json() : []).then(setMemberOptions).catch(()=>{}) }, [showAddMember])
  // When there are unsaved changes, also reset discord "done" state
  useEffect(() => { if (!saved) setDiscordDone(false) }, [saved])

  // Poll confirmations when a discord post exists
  useEffect(() => {
    if (!discordPosted) return
    const load = () => {
      fetch(`${base}/confirmations`)
        .then(r => r.ok ? r.json() : {})
        .then(setConfirmations)
        .catch(() => {})
      // Staff-only; the Raid Helper side has no such endpoint and answers 404.
      fetch(`${base}/decline-reasons`)
        .then(r => r.ok ? r.json() : {})
        .then(setDeclineReasons)
        .catch(() => {})
    }
    load()
    const id = setInterval(load, 10_000)
    return () => clearInterval(id)
  }, [event.id, discordPosted])

  // Close dropdowns on outside click
  useEffect(() => {
    if (!contextMenu) return
    const close = () => setContextMenu(null)
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [contextMenu])
  useEffect(() => {
    if (!showAddSquad) return
    const close = (e: MouseEvent) => { if (addSquadRef.current && !addSquadRef.current.contains(e.target as Node)) setShowAddSquad(false) }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [showAddSquad])
  useEffect(() => {
    if (!configSquadId) return
    const close = (e: MouseEvent) => { if (configRef.current && !configRef.current.contains(e.target as Node)) { setConfigSquadId(null); setConfigButtonRect(null) } }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [configSquadId])

  // ── Squad operations ──
  const addSquadOfType = (type: SquadType) => {
    const id = `sq${Date.now()}`
    setSquads(prev => [...prev, { id, name: type.name, type: type.key, slots: makeSlots(defaultSlots(type).map(role => ({ role }))) }])
    setShowAddSquad(false); setSaved(false)
  }

  const removeSquad = (squadId: string) => {
    const sq = squads.find(s => s.id === squadId)
    if (!sq) return
    const toUnassign = sq.slots.map(sl => sl.userId).filter(Boolean) as string[]
    setUnassigned(prev => [...prev, ...toUnassign])
    setSquads(prev => prev.filter(s => s.id !== squadId))
    setSaved(false)
  }

  const moveSquadDir = (squadId: string, dir: -1 | 1) => {
    setSquads(prev => {
      const idx = prev.findIndex(s => s.id === squadId)
      if (idx < 0) return prev
      const nIdx = idx + dir
      if (nIdx < 0 || nIdx >= prev.length) return prev
      const arr = [...prev];
      [arr[idx], arr[nIdx]] = [arr[nIdx], arr[idx]]
      return arr
    })
    setSaved(false)
  }

  // Sürüklenen takımı üstünden geçilen takımın yerine koyar. Genişlikler farklı
  // olduğunda yer değişiminden sonra imleç hâlâ aynı kartın üstünde kalabilir;
  // o karta ikinci kez bakılmaz, yoksa iki kart her karede yer değiştirirdi.
  const dragSquadOver = (targetId: string) => {
    if (!draggingSquad) return
    if (targetId === draggingSquad) { squadSwapRef.current = null; return }
    if (squadSwapRef.current === targetId) return
    squadSwapRef.current = targetId
    setSquads(prev => {
      const from = prev.findIndex(s => s.id === draggingSquad)
      const to   = prev.findIndex(s => s.id === targetId)
      if (from < 0 || to < 0) return prev
      const arr = [...prev]
      const [moved] = arr.splice(from, 1)
      arr.splice(to, 0, moved)
      return arr
    })
    setSaved(false)
  }
  const endSquadDrag = () => { setDraggingSquad(null); squadSwapRef.current = null }
  // Kart sürükleme sırasında DOM'da yer değiştirdiği için kaynağın dragend'i
  // her tarayıcıda gelmeyebilir; pencere düzeyinde de kapatılır.
  useEffect(() => {
    if (!draggingSquad) return
    window.addEventListener('dragend', endSquadDrag)
    window.addEventListener('drop', endSquadDrag)
    return () => {
      window.removeEventListener('dragend', endSquadDrag)
      window.removeEventListener('drop', endSquadDrag)
    }
  }, [draggingSquad])

  const startEdit = (sq: Squad) => { setEditingId(sq.id); setEditName(sq.name) }
  const commitEdit = () => {
    if (!editingId) return
    setSquads(prev => prev.map(sq => sq.id === editingId ? { ...sq, name: editName.trim() || sq.name } : sq))
    setEditingId(null)
  }

  // ── Slot count adjustment ──
  const adjustSlotCount = (squadId: string, role: string, delta: number) => {
    setSquads(prev => prev.map(sq => {
      if (sq.id !== squadId) return sq
      const roleSlots = sq.slots.filter(s => s.role === role)
      if (delta > 0) {
        const newSlot: Slot = { id: `s${Date.now()}_${Math.random().toString(36).slice(2)}`, role, userId: null }
        return { ...sq, slots: [...sq.slots, newSlot] }
      } else if (delta < 0 && roleSlots.length > 0) {
        const lastIdx = sq.slots.map((s,i)=>s.role===role?i:-1).filter(i=>i>=0).at(-1)!
        const lastSlot = sq.slots[lastIdx]
        if (lastSlot.userId) setUnassigned(prev=>[...prev, lastSlot.userId!])
        return { ...sq, slots: sq.slots.filter((_,i) => i !== lastIdx) }
      }
      return sq
    }))
    setSaved(false)
  }

  // ── Player movement via drag ──
  // Hedef slot doluysa yer değiştirir: oradaki oyuncu, taşınan oyuncunun geldiği
  // yere (slot / havuz) döner. Boşsa düz taşıma.
  const fillSlot = (userId: string, from: string | null, fromSlotId: string | undefined, toSquadId: string, toSlotId: string) => {
    const targetSlot = squads.find(s => s.id === toSquadId)?.slots.find(sl => sl.id === toSlotId)
    const displaced = targetSlot?.userId && targetSlot.userId !== userId ? targetSlot.userId : null
    // Remove from source (ve yer değiştirilen oyuncuyu kaynağa koy)
    if (from === null) setUnassigned(prev => displaced ? [...prev.filter(id => id !== userId), displaced] : prev.filter(id => id !== userId))
    else if (from === 'removed') setRemovedPool(prev => displaced ? [...prev.filter(id => id !== userId), displaced] : prev.filter(id => id !== userId))
    else setSquads(prev => prev.map(sq => sq.id === from
      ? { ...sq, slots: sq.slots.map(sl => sl.id === fromSlotId ? { ...sl, userId: displaced } : sl) }
      : sq))
    // Fill target
    setSquads(prev => prev.map(sq => sq.id === toSquadId
      ? { ...sq, slots: sq.slots.map(sl => sl.id === toSlotId ? { ...sl, userId } : sl) }
      : sq))
    setSaved(false)
  }

  const fillFirstEmptySlot = (userId: string, from: string | null, fromSlotId: string | undefined, toSquadId: string) => {
    const sq = squads.find(s => s.id === toSquadId)
    if (!sq) return
    const emptySlot = sq.slots.find(sl => !sl.userId)
    if (!emptySlot) return
    fillSlot(userId, from, fromSlotId, toSquadId, emptySlot.id)
  }

  const returnToPool = (userId: string, fromSquadId: string, fromSlotId: string) => {
    setSquads(prev => prev.map(sq => sq.id === fromSquadId
      ? { ...sq, slots: sq.slots.map(sl => sl.id === fromSlotId ? { ...sl, userId: null } : sl) }
      : sq))
    setUnassigned(prev => [...prev, userId])
    setSaved(false)
  }

  const handleDropToSlot = (toSquadId: string, toSlotId: string) => {
    if (!dragging) return
    fillSlot(dragging.userId, dragging.from, dragging.slotId, toSquadId, toSlotId)
    setDragging(null); setDragOver(null)
  }

  const handleDropToSquad = (toSquadId: string) => {
    if (!dragging) return
    fillFirstEmptySlot(dragging.userId, dragging.from, dragging.slotId, toSquadId)
    setDragging(null); setDragOver(null)
  }

  const movePlayerToPool = (dp: DraggingPlayer, pool: 'unassigned' | 'removed') => {
    const { userId, from, slotId } = dp
    if (from === null && pool === 'unassigned') return
    if (from === 'removed' && pool === 'removed') return
    // Remove from source
    if (from === null) setUnassigned(prev => prev.filter(id => id !== userId))
    else if (from === 'removed') setRemovedPool(prev => prev.filter(id => id !== userId))
    else setSquads(prev => prev.map(sq => sq.id === from
      ? { ...sq, slots: sq.slots.map(sl => sl.id === slotId ? { ...sl, userId: null } : sl) }
      : sq))
    if (pool === 'removed') setRemovedPool(prev => [...prev, userId])
    else setUnassigned(prev => [...prev, userId])
    setSaved(false)
  }

  const handleDropToPool = (pool: 'unassigned' | 'removed') => {
    if (!dragging) return
    movePlayerToPool(dragging, pool)
    setDragging(null); setDragOver(null)
  }

  // ── Dokun-yerleştir hedefleri (mobil): seçili oyuncuyu taşır ──
  const placePickedToSlot = (toSquadId: string, toSlotId: string) => {
    if (!picked) return
    fillSlot(picked.userId, picked.from, picked.slotId, toSquadId, toSlotId)
    setPicked(null)
  }
  const placePickedToPool = (pool: 'unassigned' | 'removed') => {
    if (!picked) return
    movePlayerToPool(picked, pool)
    setPicked(null)
  }

  // ── Role overrides ──
  const toggleRole = (userId: string, roleId: string) => {
    setRoleOverrides(prev => {
      const cur = prev[userId] ?? []
      const upd = cur.includes(roleId) ? cur.filter(r => r !== roleId) : [...cur, roleId]
      return { ...prev, [userId]: upd }
    }); setSaved(false)
  }

  const openContextMenu = (e: React.MouseEvent, userId: string) => {
    e.preventDefault(); e.stopPropagation()
    setContextMenu({ userId, x: e.clientX, y: e.clientY })
  }

  // ── Add member ──
  const addMember = (member: MemberOption) => {
    // Passive player (tentative/absent) — promote to unassigned
    if (passivePlayerIds.has(member.id)) {
      const existing = playerMap[member.id]
      if (existing && !customPlayers.some(cp => cp.user_id === member.id)) {
        setCustomPlayers(prev => [...prev, existing])
      }
      setUnassigned(prev => prev.includes(member.id) ? prev : [...prev, member.id])
      setShowAddMember(false); setMemberSearch(''); setSaved(false)
      return
    }
    if (playerMap[member.id]) return
    const custom: SignUp = {
      user_id: member.id, name: member.display_name || member.username,
      class_name: '', class_emote_id: null, spec_name: null, spec_emote_id: null,
      is_active: true, avatar_url: member.avatar_url, in_roster: false,
    }
    setCustomPlayers(prev => [...prev, custom])
    setUnassigned(prev => [...prev, member.id])
    setShowAddMember(false); setMemberSearch(''); setSaved(false)
  }

  const removeCustomPlayer = (userId: string) => {
    setCustomPlayers(prev => prev.filter(p => p.user_id !== userId))
    setUnassigned(prev => prev.filter(id => id !== userId))
    setRemovedPool(prev => prev.filter(id => id !== userId))
    setSquads(prev => prev.map(sq => ({ ...sq, slots: sq.slots.map(sl => sl.userId === userId ? { ...sl, userId: null } : sl) })))
    setSaved(false)
  }

  // ── Save ──
  // `substitute_players` is what the Raid Helper side reads as bench; matches
  // work it out themselves from the same rule (not placed, not removed).
  const configBody = () => ({
    squads, custom_players: customPlayers, role_overrides: roleOverrides,
    substitute_players: unassigned, removed_players: removedPool,
  })
  const saveConfig = async () => {
    setSaving(true)
    try {
      await fetch(`${base}/squads`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(configBody()),
      })
      setSaved(true)
    } finally { setSaving(false) }
  }

  // ── Discord send/update ──
  const sendToDiscord = async () => {
    setDiscordSending(true)
    setDiscordDone(false)
    try {
      // Auto-save first if there are unsaved changes
      if (!saved) {
        await fetch(`${base}/squads`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(configBody()),
        })
        setSaved(true)
      }
      const method = discordPosted ? 'PATCH' : 'POST'
      const res = await fetch(`${base}/discord-post`, { method })
      if (res.ok) { setDiscordPosted(true); setDiscordDone(true) }
    } finally { setDiscordSending(false) }
  }

  const assignedIds = new Set([
    ...unassigned,
    ...removedPool,
    ...squads.flatMap(sq => sq.slots.map(sl => sl.userId).filter(Boolean) as string[]),
  ])

  const filteredMembers = memberOptions.filter(m => {
    // Active/bench players already in the builder → exclude
    if (playerMap[m.id] && !passivePlayerIds.has(m.id)) return false
    // Passive player already promoted to unassigned/bench/slot → exclude
    if (passivePlayerIds.has(m.id) && assignedIds.has(m.id)) return false
    const q = memberSearch.toLowerCase()
    const label = (m.display_name || m.username || '').toLowerCase()
    return !q || label.includes(q) || m.username.toLowerCase().includes(q)
  })

  const passiveSorted = [
    ...passivePlayers.filter(s => s.class_name === 'Tentative' && !assignedIds.has(s.user_id)),
    ...passivePlayers.filter(s => s.class_name !== 'Tentative' && !assignedIds.has(s.user_id)),
  ]

  const totalPlaced = squads.reduce((sum, sq) => sum + sq.slots.filter(sl => sl.userId).length, 0)

  if (!loaded) return <div className="flex items-center justify-center h-full text-sm text-muted-foreground">{T.squadBuilder.loading}</div>

  return (
    <div className="relative flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-white/10 bg-black/25 px-5 py-4 backdrop-blur-sm">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2.5 font-display text-[11px] font-bold uppercase tracking-[0.18em] text-foreground/70">
            <span aria-hidden className="h-px w-6 shrink-0 bg-primary" />
            {T.squadBuilder.rosterConfiguration}
          </h2>
          {/* Maç ekranından gelen kadro başlıksız açılır (RosterConfig boş bir
              etkinlik kurar); o zaman alt satır hiç basılmaz. */}
          {(event.title || event.start_time || event.date) && (
            <p className="mt-1 truncate pl-[2.125rem] text-xs text-muted-foreground">
              {event.title}
              {event.title && (event.start_time || event.date) ? ' · ' : ''}
              {event.start_time ? formatDate(event.start_time) : event.date}
            </p>
          )}
        </div>
        <div className="ml-auto flex shrink-0 flex-wrap items-center gap-1.5">
          <button className={panelGhostButton} onClick={() => setShowAddMember(true)}>
            <UserPlus className="h-3.5 w-3.5" /> {T.squadBuilder.addMember}
          </button>
          <div className="relative" ref={addSquadRef}>
            <button className={panelGhostButton} onClick={() => setShowAddSquad(v => !v)}>
              <ChevronDown className="h-3.5 w-3.5" /> {T.squadBuilder.addSquad}
            </button>
            {showAddSquad && (
              <div className={`absolute right-0 top-full z-30 mt-1.5 min-w-40 overflow-hidden ${popoverSurface}`}>
                {catalog.squad_types.map(t => (
                  <button key={t.key} onClick={() => addSquadOfType(t)}
                    className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-white/10 transition-colors text-left">
                    <EmojiGlyph emoji={t.emoji} size={20} />
                    <span className="text-sm">{t.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button className={saved ? panelGhostButton : panelPrimaryButton} onClick={saveConfig} disabled={saving}>
            {saved ? <><Check className="h-3.5 w-3.5" /> {T.squadBuilder.saved}</> : <><Save className="h-3.5 w-3.5" /> {T.squadBuilder.save}</>}
          </button>
          {/* Discord'un moru bir marka rengidir, panelin paleti değil: şekli
              bandın düğmelerine uyar, rengi Discord'un kalır. Strateji Ekibi
              kadroyu düzenler ama Discord'a gönderemez, o yüzden düğme gizli. */}
          {canSend && (
          <button onClick={sendToDiscord} disabled={discordSending}
            title={discordPosted ? T.squadBuilder.updateDiscordTitle : T.squadBuilder.sendDiscordTitle}
            className={`flex h-8 shrink-0 cursor-pointer items-center gap-1.5 rounded-sm px-3 font-display text-[11px] font-bold uppercase tracking-[0.14em] transition-colors disabled:opacity-40 ${
              discordDone
                ? 'border border-white/10 bg-white/5 text-muted-foreground hover:bg-white/10 hover:text-foreground'
                : 'bg-[#5865F2] text-white hover:bg-[#4752C4]'
            }`}>
            {discordSending
              ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> {discordPosted ? T.squadBuilder.updating : T.squadBuilder.sending}</>
              : discordDone
                ? <><Check className="h-3.5 w-3.5" /> {discordPosted ? T.squadBuilder.updated : T.squadBuilder.sent}</>
                : discordPosted
                  ? <><RefreshCw className="h-3.5 w-3.5" /> {T.squadBuilder.update}</>
                  : <><DiscordIcon className="h-3.5 w-3.5" /> {T.squadBuilder.send}</>
            }
          </button>
          )}
        </div>
      </div>

      {/* Add Member Modal */}
      {showAddMember && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => { setShowAddMember(false); setMemberSearch('') }}>
          <div className={`w-80 ${popoverSurface}`} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <p className={`${sectionLabel} text-foreground/70`}>{T.squadBuilder.addMember}</p>
              <button onClick={() => { setShowAddMember(false); setMemberSearch('') }} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-3">
              <div className="relative mb-2">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <input autoFocus value={memberSearch} onChange={e => setMemberSearch(e.target.value)} placeholder={T.squadBuilder.searchMembers}
                  className="w-full rounded-sm border border-white/10 bg-white/5 py-1.5 pl-8 pr-3 text-sm outline-none placeholder:text-muted-foreground/50 focus:border-primary" />
              </div>
              <div className="max-h-60 overflow-y-auto space-y-0.5">
                {filteredMembers.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">{T.squadBuilder.noMembersFound}</p>}
                {filteredMembers.map(m => {
                  const label = m.display_name || m.username
                  const passiveSignUp = passivePlayerIds.has(m.id) ? playerMap[m.id] : null
                  return (
                    <button key={m.id} onClick={() => addMember(m)}
                      className="w-full flex items-center gap-2.5 rounded-sm px-2.5 py-1.5 hover:bg-white/10 transition-colors text-left">
                      {m.avatar_url ? <img src={m.avatar_url} draggable={false} className="h-7 w-7 rounded-full object-cover shrink-0 pointer-events-none select-none" /> :
                        <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center text-xs shrink-0">{label[0]?.toUpperCase()}</div>}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{label}</p>
                        <p className="text-[10px] text-muted-foreground">{passiveSignUp ? passiveSignUp.class_name : m.team}</p>
                      </div>
                      {passiveSignUp && <PlayerIcon su={passiveSignUp} emoji={passiveSignUp.class_emoji} />}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Right-click context menu */}
      {contextMenu && (
        <>
          <div className="fixed inset-0 z-40" onMouseDown={() => setContextMenu(null)} />
          <div className={`fixed z-50 p-2 ${popoverSurface}`}
            style={{ left: contextMenu.x, top: contextMenu.y }} onMouseDown={e => e.stopPropagation()}>
            <p className={`${sectionLabel} px-1 pb-1.5 text-muted-foreground/60`}>{T.squadBuilder.extraRoles}</p>
            {/* The sub-classes from Settings, by position. Catalog roles only
                when no position has any sub-class yet. */}
            {matchSpecs.groups.map(group => (
              <div key={group.className} className="mb-1.5 last:mb-0">
                <p className="flex items-center gap-1 px-1 pb-1 text-[10px] text-muted-foreground/70">
                  <EmojiGlyph emoji={group.classEmoji} size={12} />
                  {group.className}
                </p>
                <div className="grid grid-cols-4 gap-1">
                  {group.specs.map(spec => {
                    const active = (roleOverrides[contextMenu.userId] ?? []).includes(spec.id)
                    return (
                      <button key={spec.id} onClick={() => toggleRole(contextMenu.userId, spec.id)}
                        className={`flex flex-col items-center gap-1 p-2 rounded-sm transition-colors min-w-14 ${active ? 'bg-primary/20 border border-primary/40' : 'border border-transparent hover:bg-white/10'}`}>
                        {spec.emoji && <EmojiGlyph emoji={spec.emoji} size={24} />}
                        <span className="text-[9px] text-muted-foreground leading-none text-center">{spec.name}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}
            <div className={`grid grid-cols-4 gap-1 ${matchSpecs.groups.length ? 'hidden' : ''}`}>
              {catalog.roles.map(role => {
                const active = (roleOverrides[contextMenu.userId] ?? []).includes(role.key)
                return (
                  <button key={role.key} onClick={() => toggleRole(contextMenu.userId, role.key)}
                    className={`flex flex-col items-center gap-1 p-2 rounded-sm transition-colors min-w-14 ${active ? 'bg-primary/20 border border-primary/40' : 'border border-transparent hover:bg-white/10'}`}>
                    <EmojiGlyph emoji={role.emoji} size={24} />
                    <span className="text-[9px] text-muted-foreground leading-none text-center">{role.name}</span>
                  </button>
                )
              })}
            </div>
          </div>
        </>
      )}

      {/* Fixed config popup — rendered at root to escape overflow containers */}
      {configSquadId && configButtonRect && (() => {
        const sq = squads.find(s => s.id === configSquadId)
        if (!sq) return null
        const configRules = typeOf(catalog, sq.type)?.rules ?? []
        return (
          <div ref={configRef}
            className={`fixed z-50 min-w-44 p-3 ${popoverSurface}`}
            style={{ left: configButtonRect.right - 176, top: configButtonRect.bottom + 4 }}
            onMouseDown={e => e.stopPropagation()}>
            <p className={`${sectionLabel} mb-2 text-muted-foreground/60`}>{T.squadBuilder.slotConfig}</p>
            <div className="space-y-1.5">
              {configRules.map(rule => {
                const count = sq.slots.filter(s => s.role === rule.role).length
                return (
                  <div key={rule.role} className="flex items-center gap-2">
                    <SlotIcon role={rule.role} />
                    <span className="text-xs text-muted-foreground flex-1">{roleOf(catalog, rule.role)?.name ?? rule.role}</span>
                    {rule.min === rule.max ? (
                      <span className="text-xs text-muted-foreground/50">{count}</span>
                    ) : (
                      <div className="flex items-center gap-1">
                        <button onClick={() => adjustSlotCount(sq.id, rule.role, -1)} disabled={count <= (rule.min ?? 0)}
                          className="flex h-5 w-5 items-center justify-center rounded-sm bg-white/10 text-xs transition-colors hover:bg-white/20 disabled:opacity-30">−</button>
                        <span className="text-xs w-4 text-center">{count}</span>
                        <button onClick={() => adjustSlotCount(sq.id, rule.role, 1)} disabled={count >= (rule.max ?? 99)}
                          className="flex h-5 w-5 items-center justify-center rounded-sm bg-white/10 text-xs transition-colors hover:bg-white/20 disabled:opacity-30">+</button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )
      })()}

      {/* Body — dar ekranda üç panel yatay kaydırılır (sürükle-bırak dokunmada
          çalışmaz, ama kadro okunabilir); md+ tek ekranda yan yana. Dikey taşma
          her zaman panellerin kendi iç scroll'una bırakılır. */}
      <div className="flex flex-1 overflow-x-auto overflow-y-hidden md:overflow-x-hidden">
        {/* Left panel */}
        <div className={`w-56 shrink-0 border-r border-white/10 flex flex-col overflow-hidden ${sidePanel}`}>
          <div className="shrink-0 px-3 pt-2.5 pb-1.5">
            <p className={`${sectionLabel} text-muted-foreground/60`}>{T.squadBuilder.unassigned.replace('{n}', String(unassigned.length))}</p>
            <p className="mb-1.5 mt-0.5 text-[10px] leading-snug text-muted-foreground/45">{T.squadBuilder.unassignedHint}</p>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground/50 pointer-events-none" />
              <input
                value={unassignedSearch}
                onChange={e => setUnassignedSearch(e.target.value)}
                placeholder={T.squadBuilder.search}
                className="w-full rounded-sm border border-white/10 bg-background/40 py-1 pl-6 pr-2 text-xs outline-none placeholder:text-muted-foreground/40 focus:border-primary"
              />
            </div>
          </div>
          <div onDragOver={e=>{if(draggingSquad)return;e.preventDefault();setDragOver('__unassigned__')}} onDragLeave={()=>setDragOver(null)}
            onDrop={()=>handleDropToPool('unassigned')}
            onClick={isMobile && picked ? () => placePickedToPool('unassigned') : undefined}
            className={`flex-1 overflow-y-auto px-2 pb-1 space-y-1 min-h-0 transition-colors ${dragOver==='__unassigned__'?'bg-primary/5':''} ${isMobile && picked ? 'ring-1 ring-inset ring-primary/25' : ''}`}>
            {unassigned.length === 0 && <p className="text-xs text-muted-foreground/30 text-center py-4">{T.squadBuilder.allAssigned}</p>}
            {(() => {
              // Players first, bench sign-ups below them; the sort is stable,
              // so each group keeps the sign-up order.
              const q = unassignedSearch.toLowerCase()
              const benchRank = (uid: string) => { const p = playerMap[uid]; return p && isBench(p) ? 1 : 0 }
              return [...unassigned]
                .sort((a, b) => benchRank(a) - benchRank(b))
                .filter(uid => !q || (playerMap[uid]?.name.toLowerCase().includes(q)))
                .map(uid => {
                  const p = playerMap[uid]; if (!p) return null
                  const isCustom = customPlayers.some(cp => cp.user_id === uid)
                  return (
                    <UnassignedRow key={uid} player={p} isCustom={isCustom} bench={isBench(p)}
                      confirmStatus={confirmations[uid]} declineReason={declineReasons[uid]}
                      tapMode={isMobile} isPicked={picked?.userId === uid}
                      onTap={() => picked && picked.userId !== uid
                        ? placePickedToPool('unassigned')
                        : togglePick({ userId: uid, from: null })}
                      onDragStart={() => setDragging({ userId: uid, from: null })}
                      onRemoveCustom={isCustom ? () => removeCustomPlayer(uid) : undefined} />
                  )
                })
            })()}
          </div>

          {/* Kadrodan çıkarılanlar: ne takımda ne yedekte. Reddeden birini
              yedek olarak da sorulmasın diye buraya bırakırsın; Discord
              gönderisine hiç girmez. */}
          <div className="shrink-0 border-t border-white/10">
            <p className={`${sectionLabel} px-3 py-2 text-muted-foreground/60`}>{T.squadBuilder.removed.replace('{n}', String(removedPool.length))}</p>
            <div onDragOver={e=>{if(draggingSquad)return;e.preventDefault();setDragOver('removed')}} onDragLeave={()=>setDragOver(null)}
              onDrop={()=>handleDropToPool('removed')}
              onClick={isMobile && picked ? () => placePickedToPool('removed') : undefined}
              className={`max-h-40 min-h-[36px] space-y-1 overflow-y-auto px-2 pb-2 transition-colors ${dragOver==='removed'?'mx-1 rounded-sm border border-dashed border-destructive/50 bg-destructive/10':''} ${isMobile && picked ? 'rounded-sm ring-1 ring-inset ring-destructive/30' : ''}`}>
              {removedPool.length === 0 && dragOver !== 'removed' && (
                <p className="px-1 py-2 text-center text-[10px] text-muted-foreground/35">{T.squadBuilder.removedEmpty}</p>
              )}
              {removedPool.map(uid => {
                const p = playerMap[uid]; if (!p) return null
                return <UnassignedRow key={uid} player={p} removed bench={isBench(p)}
                  confirmStatus={confirmations[uid]} declineReason={declineReasons[uid]}
                  tapMode={isMobile} isPicked={picked?.userId === uid}
                  onTap={() => picked && picked.userId !== uid
                    ? placePickedToPool('removed')
                    : togglePick({ userId: uid, from: 'removed' })}
                  onDragStart={() => setDragging({ userId: uid, from: 'removed' })} />
              })}
            </div>
          </div>

          {passiveSorted.length > 0 && (
            <div className="shrink-0 border-t border-white/10 max-h-44 overflow-y-auto">
              {passiveSorted.filter(p => p.class_name === 'Tentative').length > 0 && (
                <>
                  <p className={`${sectionLabel} px-3 py-1.5 text-muted-foreground/40`}>{T.squadBuilder.tentative}</p>
                  <div className="px-2 space-y-0.5 pb-1">
                    {passiveSorted.filter(p => p.class_name === 'Tentative').map(p =>
                      <PassiveRow key={p.user_id} player={p} />)}
                  </div>
                </>
              )}
              {passiveSorted.filter(p => p.class_name !== 'Tentative').length > 0 && (
                <>
                  <p className={`${sectionLabel} px-3 py-1.5 text-muted-foreground/30`}>{T.squadBuilder.absent}</p>
                  <div className="px-2 space-y-0.5 pb-2">
                    {passiveSorted.filter(p => p.class_name !== 'Tentative').map(p =>
                      <PassiveRow key={p.user_id} player={p} />)}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Centre: squads — dar ekranda çökmesin diye min genişlik */}
        <div className="min-w-[280px] flex-1 overflow-y-auto p-4 md:min-w-0">
          <div className="flex flex-wrap gap-3 content-start">
            {squads.map((sq, sqIdx) => (
              <SquadCard
                key={sq.id} squad={sq} squads={squads}
                playerMap={playerMap} roleOverrides={roleOverrides}
                confirmations={confirmations}
                declineReasons={declineReasons}
                dragOver={dragOver}
                isEditing={editingId === sq.id} editName={editName} nameInputRef={nameInputRef}
                isFirst={sqIdx === 0} isLast={sqIdx === squads.length - 1}
                showConfig={configSquadId === sq.id} configRef={configRef}
                onStartEdit={() => startEdit(sq)} onCommitEdit={commitEdit} onEditName={setEditName}
                onRemove={() => removeSquad(sq.id)}
                onMoveLeft={() => moveSquadDir(sq.id, -1)} onMoveRight={() => moveSquadDir(sq.id, 1)}
                isSquadDragging={draggingSquad === sq.id} squadDragActive={!!draggingSquad}
                onSquadDragStart={() => { setDraggingSquad(sq.id); squadSwapRef.current = null }}
                onSquadDragOver={() => dragSquadOver(sq.id)}
                onSquadDragEnd={endSquadDrag}
                onToggleConfig={(e: React.MouseEvent) => {
                  if (configSquadId === sq.id) { setConfigSquadId(null); setConfigButtonRect(null) }
                  else { setConfigSquadId(sq.id); setConfigButtonRect((e.currentTarget as HTMLElement).getBoundingClientRect()) }
                }}
                tapMode={isMobile} pickedUserId={picked?.userId ?? null} hasPicked={!!picked}
                onPickPlayer={(uid, slotId) => togglePick({ userId: uid, from: sq.id, slotId })}
                onPlaceToSlot={(slotId) => placePickedToSlot(sq.id, slotId)}
                onDragStartPlayer={(uid, slotId) => setDragging({ userId: uid, from: sq.id, slotId })}
                onDragOverSlot={(slotId) => setDragOver(slotId)}
                onDragLeaveSlot={() => setDragOver(null)}
                onDropToSlot={(slotId) => handleDropToSlot(sq.id, slotId)}
                onDragOverSquad={() => setDragOver(`squad_${sq.id}`)}
                onDragLeaveSquad={() => setDragOver(null)}
                onDropToSquad={() => handleDropToSquad(sq.id)}
                onDoubleClickSlot={(uid, slotId) => returnToPool(uid, sq.id, slotId)}
                onContextMenu={openContextMenu}
              />
            ))}
            {squads.length === 0 && <p className="text-sm text-muted-foreground/40 py-8 px-4">{T.squadBuilder.noSquads}</p>}
          </div>
        </div>

        {/* Right: overview sidebar */}
        <div className={`w-44 shrink-0 border-l border-white/10 flex flex-col overflow-hidden ${sidePanel}`}>
          <div className="shrink-0 px-3 pt-2.5 pb-1.5 border-b border-white/10">
            <p className={`${sectionLabel} text-muted-foreground/60`}>{T.squadBuilder.overview}</p>
          </div>
          {/* Total */}
          <div className="px-3 py-2 border-b border-white/10">
            {(() => {
              const totalSlots = squads.reduce((s, sq) => s + sq.slots.length, 0)
              const pct = totalSlots > 0 ? Math.round((totalPlaced / totalSlots) * 100) : 0
              return (
                <>
                  <div className="flex items-baseline justify-between mb-1.5">
                    <span className={`${sectionLabel} text-muted-foreground/50`}>{T.squadBuilder.total}</span>
                    <span className="text-sm font-bold text-foreground">{totalPlaced}<span className="text-xs font-normal text-muted-foreground/50">/{totalSlots}</span></span>
                  </div>
                  <div className="h-1 overflow-hidden rounded-sm bg-white/10">
                    <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </>
              )
            })()}
          </div>
          {/* Per-type rows */}
          <div className="flex-1 overflow-y-auto py-1">
            {(() => {
              // Players placed per squad type, in the order Settings lists them.
              const placed = (type: string) => squads
                .filter(sq => sq.type === type)
                .reduce((n, sq) => n + sq.slots.filter(sl => sl.userId).length, 0)
              return catalog.squad_types.map(t => {
                const val = placed(t.key)
                return (
                  <div key={t.key} className="flex items-center gap-2 px-3 py-1.5">
                    <span className="inline-flex shrink-0 opacity-70"><EmojiGlyph emoji={t.emoji} size={14} /></span>
                    <span className="text-[11px] flex-1 min-w-0 text-muted-foreground/70">{t.name}</span>
                    <span className={`text-[11px] font-semibold shrink-0 tabular-nums ${val > 0 ? 'text-primary' : 'text-muted-foreground/30'}`}>
                      {val}
                    </span>
                  </div>
                )
              })
            })()}
          </div>
        </div>
      </div>

      {/* Mobil: seçili oyuncu şeridi — nereye dokunulacağını söyler, bırakma yolu verir */}
      {isMobile && picked && (
        <div className="absolute inset-x-0 bottom-0 z-50 flex items-center gap-3 border-t border-primary/40 bg-popover/95 px-4 py-2.5 backdrop-blur-sm">
          <span aria-hidden className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-primary" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold">{playerMap[picked.userId]?.name ?? ''}</p>
            <p className="truncate text-[11px] text-muted-foreground">{T.squadBuilder.tapPlaceHint}</p>
          </div>
          <button onClick={() => setPicked(null)} className={`${panelGhostButton} shrink-0`}>
            {T.squadBuilder.tapCancel}
          </button>
        </div>
      )}
    </div>
  )
}

// ─── UnassignedRow ────────────────────────────────────────────────────────────

function UnassignedRow({ player, isCustom, bench, removed, confirmStatus, declineReason, tapMode, isPicked, onTap, onDragStart, onRemoveCustom }: {
  player: SignUp; isCustom?: boolean
  /** Signed up as bench: listed under the players, tagged on the right. */
  bench?: boolean
  /** In "Kadrodan Çıkarılanlar": drawn dimmed, it is out of the roster. */
  removed?: boolean
  confirmStatus?: string; declineReason?: string
  /** Mobil: sürükleme yerine dokun-seç. */
  tapMode?: boolean; isPicked?: boolean; onTap?: () => void
  onDragStart: () => void; onRemoveCustom?: () => void
}) {
  const { T } = useLanguage()
  const wanted = useSignUpSpec(player)
  return (
    <div
      draggable={!tapMode}
      onDragStart={tapMode ? undefined : onDragStart}
      onClick={tapMode ? (e) => { e.stopPropagation(); onTap?.() } : undefined}
      onContextMenu={tapMode ? (e) => e.preventDefault() : undefined}
      title={declineReason ? T.squadBuilder.declinedReason.replace('{reason}', declineReason) : undefined}
      className={`group select-none rounded-sm border px-2 py-1.5 transition-colors ${rowSurface} hover:border-white/25 ${removed ? 'opacity-60' : ''} ${tapMode ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing'} ${isPicked ? 'border-primary bg-primary/15 ring-1 ring-primary' : ''}`}>
      <div className="flex items-center gap-2">
        {!tapMode && <GripVertical className="h-3 w-3 text-muted-foreground/25 shrink-0" />}
        {confirmStatus !== undefined && <ConfirmDot status={confirmStatus} />}
        {player.avatar_url
          ? <img src={player.avatar_url} draggable={false} className="h-5 w-5 rounded-full object-cover shrink-0 pointer-events-none select-none" />
          : <div className="h-5 w-5 rounded-full bg-muted shrink-0 flex items-center justify-center text-[9px]">{player.name[0]?.toUpperCase()}</div>}
        {/* The sub-class they asked for, before the name as on the sign-up board. */}
        {wanted && <SpecMark spec={wanted} title={T.squadBuilder.wantedSpec.replace('{spec}', wanted.name)} />}
        <span className="text-xs font-medium truncate flex-1 min-w-0">{player.name}</span>
        {bench && (
          <span className="shrink-0 rounded-sm border border-white/15 px-1 py-px text-[9px] leading-none text-muted-foreground">
            {T.squadBuilder.benchTag}
          </span>
        )}
        {/* What they signed up as: the position's emoji from Settings. */}
        <span title={player.class_name} className="inline-flex shrink-0">
          <PlayerIcon su={player} emoji={player.class_emoji} />
        </span>
        {isCustom && onRemoveCustom && (
          <button onClick={e => { e.stopPropagation(); onRemoveCustom() }}
            className="opacity-0 group-hover:opacity-100 text-muted-foreground/40 hover:text-destructive transition-all ml-0.5">
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
      {declineReason && (
        <p className="mt-1 truncate pl-5 text-[10px] text-destructive/80">{declineReason}</p>
      )}
    </div>
  )
}

function PassiveRow({ player }: { player: SignUp }) {
  return (
    <div className="flex select-none items-center gap-2 rounded-sm bg-secondary/60 px-2 py-1 text-muted-foreground/50">
      <PlayerIcon su={player} emoji={player.class_emoji} />
      <span className="text-xs truncate">{player.name}</span>
    </div>
  )
}

// ─── SquadCard ────────────────────────────────────────────────────────────────

interface SquadCardProps {
  squad: Squad; squads: Squad[]
  playerMap: Record<string,SignUp>
  roleOverrides: Record<string,string[]>
  confirmations: Record<string,string>
  declineReasons: Record<string,string>
  dragOver: string | null
  isEditing: boolean; editName: string; nameInputRef: React.RefObject<HTMLInputElement|null>
  isFirst: boolean; isLast: boolean
  showConfig: boolean; configRef?: React.RefObject<HTMLDivElement|null>
  onStartEdit: () => void; onCommitEdit: () => void; onEditName: (v: string) => void
  onRemove: () => void; onMoveLeft: () => void; onMoveRight: () => void
  /** Takımın kendisini sürükleme (masaüstü). Yer, kartların üstünden geçerken canlı değişir. */
  isSquadDragging: boolean; squadDragActive: boolean
  onSquadDragStart: () => void; onSquadDragOver: () => void; onSquadDragEnd: () => void
  onToggleConfig: (e: React.MouseEvent) => void
  onDragStartPlayer: (uid: string, slotId: string) => void
  onDragOverSlot: (slotId: string) => void; onDragLeaveSlot: () => void; onDropToSlot: (slotId: string) => void
  onDragOverSquad: () => void; onDragLeaveSquad: () => void; onDropToSquad: () => void
  onDoubleClickSlot: (uid: string, slotId: string) => void
  onContextMenu: (e: React.MouseEvent, uid: string) => void
  /** Mobil: sürükleme yerine dokun-seç / dokun-yerleştir. */
  tapMode?: boolean; pickedUserId?: string | null; hasPicked?: boolean
  onPickPlayer?: (uid: string, slotId: string) => void
  /** Seçili oyuncuyu bu slota yerleştir (boşsa yerleşir, doluysa yer değiştirir). */
  onPlaceToSlot?: (slotId: string) => void
}

function SquadCard({
  squad, playerMap, roleOverrides, confirmations, declineReasons, dragOver,
  isEditing, editName, nameInputRef, isFirst, isLast,
  showConfig,
  onStartEdit, onCommitEdit, onEditName, onRemove, onMoveLeft, onMoveRight,
  isSquadDragging, squadDragActive, onSquadDragStart, onSquadDragOver, onSquadDragEnd,
  onToggleConfig,
  onDragStartPlayer, onDragOverSlot, onDragLeaveSlot, onDropToSlot,
  onDragOverSquad, onDragLeaveSquad, onDropToSquad,
  onDoubleClickSlot, onContextMenu,
  tapMode, pickedUserId, hasPicked, onPickPlayer, onPlaceToSlot,
}: SquadCardProps) {
  const squadDragOver = dragOver === `squad_${squad.id}`
  const { T } = useLanguage()
  const catalog = useRosterCatalog()
  const configRules = typeOf(catalog, squad.type)?.rules ?? []
  const cardRef = useRef<HTMLDivElement>(null)
  // Başlık tutamaktır; sürükleme görüntüsü yine de kartın tamamı olsun.
  const canDragSquad = !tapMode && !isEditing

  return (
    <div
      ref={cardRef}
      onDragOver={e => {
        e.preventDefault()
        if (squadDragActive) { e.dataTransfer.dropEffect = 'move'; onSquadDragOver(); return }
        onDragOverSquad()
      }}
      onDragLeave={squadDragActive ? undefined : onDragLeaveSquad}
      onDrop={e => { if (squadDragActive) { e.preventDefault(); onSquadDragEnd(); return } onDropToSquad() }}
      className={`w-fit min-w-52 overflow-hidden rounded-sm border shadow-sm shadow-black/40 transition-[border-color,opacity] ${squadSurface} ${
        isSquadDragging ? 'border-dashed border-primary/60 opacity-40'
        : squadDragOver || (tapMode && hasPicked) ? 'border-primary/50' : 'border-white/10'}`}
    >
      {/* Header */}
      <div
        draggable={canDragSquad}
        onDragStart={canDragSquad ? e => {
          e.dataTransfer.effectAllowed = 'move'
          e.dataTransfer.setData('text/plain', squad.id)
          if (cardRef.current) {
            const r = cardRef.current.getBoundingClientRect()
            e.dataTransfer.setDragImage(cardRef.current, e.clientX - r.left, e.clientY - r.top)
          }
          onSquadDragStart()
        } : undefined}
        onDragEnd={canDragSquad ? onSquadDragEnd : undefined}
        title={canDragSquad ? T.squadBuilder.dragSquadHint : undefined}
        className={`flex items-center gap-1.5 border-b border-white/10 bg-popover px-2.5 py-2 ${canDragSquad ? 'cursor-grab active:cursor-grabbing' : ''}`}
      >
        {canDragSquad && <GripVertical aria-hidden className="-ml-1 h-3 w-3 shrink-0 text-muted-foreground/30" />}
        <SquadTypeIcon type={squad.type} />
        {isEditing ? (
          <>
            <input ref={nameInputRef} value={editName} onChange={e => onEditName(e.target.value)}
              onBlur={onCommitEdit} onKeyDown={e => { if (e.key === 'Enter') onCommitEdit() }}
              className="flex-1 text-xs font-semibold bg-transparent border-b border-primary outline-none py-0.5 min-w-0" />
            <button onClick={onCommitEdit} className="text-primary p-0.5 shrink-0"><Check className="h-3 w-3" /></button>
          </>
        ) : (
          <>
            <button onClick={onStartEdit} className="flex-1 text-xs font-semibold text-left hover:text-primary transition-colors min-w-0 whitespace-nowrap">
              {squad.name}
            </button>
            <button onClick={onStartEdit} className="text-muted-foreground/30 hover:text-muted-foreground p-0.5 shrink-0">
              <Pencil className="h-3 w-3" />
            </button>
          </>
        )}
        {/* Slot config button */}
        {configRules.some(r => r.min !== r.max) && (
          <button onClick={e => { e.stopPropagation(); onToggleConfig(e) }}
            className={`p-0.5 shrink-0 transition-colors ${showConfig ? 'text-primary' : 'text-muted-foreground/30 hover:text-muted-foreground'}`}>
            <Settings className="h-3 w-3" />
          </button>
        )}
        {/* Dokunmatikte HTML5 sürükleme yok: orada sıra okla değişir */}
        {tapMode && (
          <>
            <button onClick={onMoveLeft} disabled={isFirst} className="text-muted-foreground/25 hover:text-muted-foreground disabled:opacity-10 p-0.5 shrink-0">
              <ChevronLeft className="h-3 w-3" />
            </button>
            <button onClick={onMoveRight} disabled={isLast} className="text-muted-foreground/25 hover:text-muted-foreground disabled:opacity-10 p-0.5 shrink-0">
              <ChevronRight className="h-3 w-3" />
            </button>
          </>
        )}
        <button onClick={onRemove} className="text-muted-foreground/25 hover:text-destructive p-0.5 shrink-0">
          <X className="h-3 w-3" />
        </button>
      </div>

      {/* Slots */}
      <div className="p-1.5 space-y-1">
        {squad.slots.length === 0 && (
          <p className="py-3 text-center text-[10px] text-muted-foreground/30">{T.squadBuilder.noSlots}</p>
        )}
        {squad.slots.map(slot => {
          const player = slot.userId ? playerMap[slot.userId] : null
          const isSlotDragOver = dragOver === slot.id
          return (
            <SlotRow
              key={slot.id}
              slot={slot}
              player={player ?? null}
              confirmStatus={slot.userId ? confirmations[slot.userId] : undefined}
              declineReason={slot.userId ? declineReasons[slot.userId] : undefined}
              extraRoles={slot.userId ? (roleOverrides[slot.userId] ?? []) : []}
              isDragOver={isSlotDragOver}
              onDragStart={slot.userId ? () => onDragStartPlayer(slot.userId!, slot.id) : undefined}
              // Dolu slot da bırakma hedefidir: üstüne başka oyuncu bırakınca
              // ikisi yer değiştirir (fillSlot swap yapar).
              // Takım sürüklenirken slotlar hedef değil, olay karta çıkar.
              onDragOver={squadDragActive ? undefined : (e) => { e.preventDefault(); e.stopPropagation(); onDragOverSlot(slot.id) }}
              onDragLeave={squadDragActive ? undefined : () => onDragLeaveSlot()}
              onDrop={squadDragActive ? undefined : (e) => { e.stopPropagation(); onDropToSlot(slot.id) }}
              onDoubleClick={slot.userId ? () => onDoubleClickSlot(slot.userId!, slot.id) : undefined}
              onContextMenu={slot.userId ? (e) => onContextMenu(e, slot.userId!) : undefined}
              tapMode={tapMode}
              isPicked={!!slot.userId && pickedUserId === slot.userId}
              tapHighlight={!!tapMode && !!hasPicked && pickedUserId !== slot.userId}
              onTap={tapMode
                ? (slot.userId
                    // Elinde başka oyuncu varsa dolu slota dokunmak yer değiştirir;
                    // yoksa bu oyuncuyu seçer.
                    ? (hasPicked && pickedUserId !== slot.userId
                        ? () => onPlaceToSlot?.(slot.id)
                        : () => onPickPlayer?.(slot.userId!, slot.id))
                    : (hasPicked ? () => onPlaceToSlot?.(slot.id) : undefined))
                : undefined}
            />
          )
        })}
      </div>
    </div>
  )
}

// ─── SlotRow ──────────────────────────────────────────────────────────────────

function SlotRow({ slot, player, confirmStatus, declineReason, extraRoles, isDragOver, tapMode, isPicked, tapHighlight, onTap, onDragStart, onDragOver, onDragLeave, onDrop, onDoubleClick, onContextMenu }: {
  slot: Slot; player: SignUp | null; confirmStatus?: string; declineReason?: string; extraRoles: string[]; isDragOver: boolean
  /** Mobil: sürükleme yerine dokun-seç / dokun-yerleştir. */
  tapMode?: boolean; isPicked?: boolean; tapHighlight?: boolean; onTap?: () => void
  onDragStart?: () => void; onDragOver?: (e: React.DragEvent) => void
  onDragLeave?: () => void; onDrop?: (e: React.DragEvent) => void
  onDoubleClick?: () => void; onContextMenu?: (e: React.MouseEvent) => void
}) {
  const { T } = useLanguage()
  const wanted = useSignUpSpec(player)
  return (
    <div
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={tapMode && onTap ? (e) => { e.stopPropagation(); onTap() } : undefined}
      className={`flex items-center gap-1.5 rounded-sm border px-1.5 py-1.5 transition-colors ${
        player
          ? confirmStatus === 'no' ? 'border-destructive/40 bg-secondary' : rowSurface
          : isDragOver || tapHighlight
            ? 'border-dashed border-primary/50 bg-primary/10'
            : 'border-dashed border-white/10 bg-popover'
      } ${isPicked ? 'ring-1 ring-primary border-primary bg-primary/15' : player && (isDragOver || tapHighlight) ? 'ring-1 ring-primary/60 border-primary/60' : ''} ${tapMode && (player || tapHighlight) ? 'cursor-pointer' : ''}`}
    >
      <SlotIcon role={slot.role} dim={!player} />

      {player ? (
        <div
          draggable={!tapMode}
          onDragStart={tapMode ? undefined : onDragStart}
          onDoubleClick={tapMode ? undefined : onDoubleClick}
          onContextMenu={tapMode ? (e) => e.preventDefault() : onContextMenu}
          title={declineReason ? T.squadBuilder.declinedReason.replace('{reason}', declineReason) : T.squadBuilder.rightClickHint}
          className={`flex-1 flex items-center gap-1.5 min-w-0 select-none ${tapMode ? '' : 'cursor-grab active:cursor-grabbing'}`}
        >
          {!tapMode && <GripVertical className="h-3 w-3 text-muted-foreground/20 shrink-0" />}
          {confirmStatus !== undefined && <ConfirmDot status={confirmStatus} />}
          {player.avatar_url
            ? <img src={player.avatar_url} draggable={false} className="h-4 w-4 rounded-full object-cover shrink-0 pointer-events-none select-none" />
            : <div className="h-4 w-4 rounded-full bg-muted shrink-0 flex items-center justify-center text-[8px]">{player.name[0]?.toUpperCase()}</div>}
          {/* What they asked for at sign-up, dimmed: the roster maker's own
              pick sits at the end of the row. Dropped once that pick is the
              same sub-class, so the icon is not drawn twice. */}
          {wanted && !extraRoles.includes(wanted.id) && (
            <SpecMark spec={wanted} dim title={T.squadBuilder.wantedSpec.replace('{spec}', wanted.name)} />
          )}
          <span className="text-xs font-medium truncate flex-1 min-w-0">{player.name}</span>
          {extraRoles.length > 0 && (
            <div className="flex items-center gap-0.5 shrink-0">
              {extraRoles.map(r => <ExtraRoleIcon key={r} roleId={r} />)}
            </div>
          )}
        </div>
      ) : (
        <span className="text-[11px] text-muted-foreground/25 flex-1">{T.eventDetail.emptySlot}</span>
      )}
    </div>
  )
}
